import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  ChevronDown,
  ChevronUp,
  Bot,
} from "lucide-react";
import { IssueStatusSelect } from "@/components/security/IssueStatusSelect";
import { IssueAssignSelect } from "@/components/security/IssueAssignSelect";
import { LinkedTickets } from "@/components/security/LinkedTickets";
import { AttackGraphVisual } from "@/components/security/AttackGraphVisual";
import { VerificationCard } from "@/components/security/issue-detail/VerificationCard";
import { getSeverityColor, formatRiskScore, getRiskScoreColor } from "@/utils/securityHelpers";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import type { SecurityIssue } from "@/types/security";

interface OverviewTabProps {
  issue: SecurityIssue;
  numericIssueId: number;
}

export function OverviewTab({ issue, numericIssueId }: OverviewTabProps) {
  const [expandedTraces, setExpandedTraces] = useState<string[]>([]);

  const toggleTrace = (traceId: string) => {
    setExpandedTraces((prev) =>
      prev.includes(traceId) ? prev.filter((id) => id !== traceId) : [...prev, traceId]
    );
  };

  return (
    <div className="mt-6 space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Description */}
          <div>
            <p className="text-sm leading-relaxed">{issue.description}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4 pt-4 border-t">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Severity</div>
              <Badge className={getSeverityColor(issue.severity)}>{issue.severity}</Badge>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Status</div>
              <IssueStatusSelect issue={issue} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Assigned To</div>
              <IssueAssignSelect issue={issue} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Risk Score</div>
              <div className={`font-semibold ${getRiskScoreColor(issue.riskScore)}`}>
                {formatRiskScore(issue.riskScore)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Scanner</div>
              <div className="text-sm font-medium">{issue.scanner}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Detected</div>
              <div className="text-sm">
                {safeFormatDistanceToNow(issue.timestamp, { addSuffix: true })}
              </div>
            </div>
            {issue.agentId && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Agent</div>
                <Link
                  to={`/agents/${issue.agentId}`}
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <Bot className="h-3.5 w-3.5" />
                  View agent
                </Link>
              </div>
            )}
          </div>

          {/* AI Verification — only render when there's actual data */}
          {issue.geminiVerification && (
            <VerificationCard
              verification={issue.geminiVerification}
              verifiedAt={issue.verifiedAt}
            />
          )}

          {/* Detected / Sanitized Content */}
          {issue.detectedContent && (
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold mb-2">Detected Content</h3>
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm font-mono break-all">
                {issue.detectedContent}
              </div>
              {issue.sanitizedContent && (
                <>
                  <h3 className="text-sm font-semibold mb-2 mt-4">
                    Sanitized Content (after LLM Guard)
                  </h3>
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm font-mono break-all">
                    {issue.sanitizedContent}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Execution Traces */}
          {issue.traces && issue.traces.length > 0 && (
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Execution Traces
              </h3>
              <div className="border rounded-lg divide-y bg-white">
                {issue.traces.map((trace) => (
                  <div key={trace.id}>
                    <div
                      className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3"
                      onClick={() => toggleTrace(trace.id)}
                    >
                      <div className="flex-shrink-0">
                        {expandedTraces.includes(trace.id) ? (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                        ) : (
                          <ChevronUp
                            className="h-3.5 w-3.5 text-gray-400"
                            style={{ transform: "rotate(-90deg)" }}
                          />
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            trace.status === "blocked"
                              ? "bg-red-500"
                              : trace.status === "warning"
                                ? "bg-yellow-500"
                                : "bg-green-500"
                          }`}
                        />
                      </div>
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 uppercase tracking-wide">
                          {trace.type}
                        </span>
                      </div>
                      <span className="text-sm font-normal flex-1">{trace.name}</span>
                      <div className="flex-shrink-0">
                        <span className="text-xs text-gray-500">
                          {(trace.duration * 1000).toFixed(0)}ms
                        </span>
                      </div>
                      <div className="flex-shrink-0">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                            trace.status === "blocked"
                              ? "bg-red-100 text-red-700"
                              : trace.status === "warning"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                          }`}
                        >
                          {trace.status}
                        </span>
                      </div>
                    </div>

                    {expandedTraces.includes(trace.id) && (
                      <div className="px-4 py-3 bg-gray-50 border-t">
                        <div className="space-y-3 ml-7">
                          {/* Metadata grid */}
                          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Type</dt>
                              <dd className="text-xs font-mono">{trace.type}</dd>
                            </div>
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Timestamp</dt>
                              <dd className="text-xs">
                                {new Date(trace.timestamp).toLocaleString()}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-[11px] text-muted-foreground">Duration</dt>
                              <dd className="text-xs tabular-nums">
                                {(trace.duration * 1000).toFixed(0)}ms
                              </dd>
                            </div>
                            {(trace.tokensIn != null || trace.tokensOut != null) && (
                              <div>
                                <dt className="text-[11px] text-muted-foreground">Tokens</dt>
                                <dd className="text-xs tabular-nums">
                                  {trace.tokensIn ?? 0} in / {trace.tokensOut ?? 0} out
                                </dd>
                              </div>
                            )}
                          </dl>

                          {/* Input/Output content */}
                          {trace.input && (
                            <div>
                              <div className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                INPUT
                              </div>
                              <div className="bg-white p-3 rounded border border-gray-200 text-xs font-mono text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                                {trace.input}
                              </div>
                            </div>
                          )}
                          {trace.output && (
                            <div>
                              <div className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                OUTPUT
                              </div>
                              <div className="bg-white p-3 rounded border border-gray-200 text-xs font-mono text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                                {trace.output}
                              </div>
                            </div>
                          )}
                          {!trace.input && !trace.output && (
                            <p className="text-xs text-muted-foreground italic">
                              No input/output content available for this trace.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <LinkedTickets issueId={numericIssueId} />

      {issue.attackFlow && issue.attackFlow.length > 0 && (
        <AttackGraphVisual attackFlow={issue.attackFlow} severity={issue.severity} />
      )}
    </div>
  );
}
